'use client';

import { Button } from '@/components/ui/button';
import { initMixpanel } from '@/lib/mixpanel';
import { logoutUser } from '@/services/userSessionService';
import { selectIsLoggedIn, userSessionStore, useUserSessionStore } from '@/stores/userSessionStore';
import { EntityStoreStatuses } from '@/util/createFetchStore';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { NEXT_PUBLIC_MODE } from '~/modules/config';

export function NavBar() {
  const router = useRouter();
  const isLoggedIn = useUserSessionStore(selectIsLoggedIn);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    router.push('/');
    logoutUser();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(function checkIsLoggedIn() {
    // Always get the latest state from the store within effects
    const { status } = userSessionStore.getState();
    switch (status) {
      case EntityStoreStatuses.UNINITIALIZED: {
        userSessionStore.fetch();
        break;
      }
      default: {
        /* no op */
      }
    }
  }, []);

  useEffect(() => {
    // mixpanel.init('ccf189e9fbff64c71dc700ea3a3558ff', {
    //   debug: true,
    //   track_pageview: true,
    //   persistence: 'localStorage',
    // });
    initMixpanel();
  }, []);

  const initHotjar = () => {
    if (typeof window === 'undefined') return;
    if ((window as any).hj) return; // already initialized

    (function (h: any, o: any, t: any, j: any, a?: any, r?: any) {
      h.hj =
        h.hj ||
        function () {
          (h.hj.q = h.hj.q || []).push(arguments);
        };
      h._hjSettings = { hjid: 6388890, hjsv: 6 }; // replace 1234567 with your Hotjar ID
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script');
      r.async = true;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  };

  useEffect(() => {
    initHotjar();
  }, []);

  return (
    <>
      <header className="bg-[#00B67A] text-white py-4">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link href="/" className="text-xl font-bold">
            WarrantyPilot {NEXT_PUBLIC_MODE === 'production' ? '' : 'DEMO'}
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            <ul className="flex space-x-6">
              <li>
                <Link href="/partner" className="hover:text-white/80">
                  Become a Partner
                </Link>
              </li>
              <li>
                <Link href="/blog" className="hover:text-white/80">
                  Blog
                </Link>
              </li>
              {isLoggedIn ? (
                <>
                  <li>
                    <Link href="/account" className="hover:text-white/80">
                      My Account
                    </Link>
                  </li>
                  <li>
                    <button onClick={handleLogout} className="hover:text-white/80 cursor-pointer">
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <li>
                  <Link href="/login" className="hover:text-white/80">
                    Login
                  </Link>
                </li>
              )}
            </ul>
          </nav>

          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu} className={`text-white`}>
              <Menu className="h-6 w-6" style={{ width: '30px', height: '30px' }} />
            </Button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black z-50"
              onClick={toggleMobileMenu}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed top-0 right-0 bottom-0 w-64 bg-white shadow-lg z-50 overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-end mb-6">
                  <Button variant="ghost" size="icon" onClick={toggleMobileMenu} className="text-[#1A3B39]">
                    <X className="h-6 w-6" />
                  </Button>
                </div>
                <nav className="flex flex-col space-y-4">
                  <Link
                    href="/"
                    className="font-medium text-[#1A3B39] hover:text-[#1A3B39]/80"
                    onClick={toggleMobileMenu}
                  >
                    Home
                  </Link>
                  <Link
                    href="/partner"
                    className="font-medium text-[#1A3B39] hover:text-[#1A3B39]/80"
                    onClick={toggleMobileMenu}
                  >
                    Become a Partner
                  </Link>
                  <Link
                    href="/blog"
                    className="font-medium text-[#1A3B39] hover:text-[#1A3B39]/80"
                    onClick={toggleMobileMenu}
                  >
                    Blog
                  </Link>
                  {isLoggedIn ? (
                    <>
                      <Link
                        href="/account"
                        className="font-medium text-[#1A3B39] hover:text-[#1A3B39]/80"
                        onClick={toggleMobileMenu}
                      >
                        My Account
                      </Link>
                      <Button
                        onClick={handleLogout}
                        className="font-medium bg-[#1A3B39] text-white hover:bg-[#1A3B39]/90 w-full"
                      >
                        Logout
                      </Button>
                    </>
                  ) : (
                    <Link href="/login" className="hover:text-white/80" onClick={toggleMobileMenu}>
                      Login
                    </Link>
                  )}
                </nav>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
